import { ReactNode, useEffect } from 'react';
import { CareMapState } from './caremap/CareMapState';
import { CareMapOnScreen } from './caremap/CareMapOnScreen';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { AlertPresenter } from './AlertPresenter';
import { HistoryViewModel } from './support/HistoryViewModel';
import { AppPrincipal, isAnonymousViewer } from './auth/AppPrincipal';
import { AnonymousPromoterState } from './auth/anonymous/AnonymousPromoterState';
import { Logger } from './support/Logger';
import { isNoCareMapAccessError, NO_CARE_MAP_ACCESS_ALERT } from './caremap/noCareMapAccess';
import { SignInToOpenCareMap } from './caremap/SignInToOpenCareMap';

export const BlockUntilActiveCaremap: React.FC<{children: ReactNode}> = ({ children }) => {
    const careMapState = useInstance(CareMapState);

    useSubscription(() => careMapState);

    return careMapState.hasActiveCareMap() ? children : null;
};

/**
 * Makes the care map active for everything beneath, and declares it the one on screen unless
 * `showsCareMap` is false: a page that keeps a care map active for its sections' sake while showing
 * something else (the agency home) passes false.
 */
export const ActiveCareMapProviderV2: React.FC<{children: ReactNode; careMapId?: string; showsCareMap?: boolean }> = ({ children, careMapId, showsCareMap = true }) => {
    const caremapState = useInstance(CareMapState);
    const alert = useInstance(AlertPresenter);
    const history = useInstance(HistoryViewModel);
    const logger = useInstance(Logger);
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const signInToOpen = useInstance(SignInToOpenCareMap);

    const promoterState = useInstance(AnonymousPromoterState);
    const isPromoting = !promoterState.state?.isComplete;
    const onScreen = useInstance(CareMapOnScreen);

    // What this page shows, for anything that must tell the care map on screen from the one last opened.
    useEffect(() => {
        if (!showsCareMap || !careMapId || careMapId === 'undefined') {
            return undefined;
        }
        onScreen.enter(careMapId);
        return () => onScreen.leave(careMapId);
    }, [careMapId, showsCareMap, onScreen]);

    useEffect(() => {
        // as a convenience for consumers, support undefined careMapId
        if (!careMapId) {
            caremapState.unsetCurrentCareMap();
            return;
        }

        // if we get here with "undefined", we mis-routed the user,
        // so return to home page without showing an error
        if (careMapId === 'undefined') {
            history.push('/');
            return;
        }

        Promise.resolve().then(async () => {
            if (caremapState.getActiveCaremap()?.careMapId !== careMapId){
                try {
                    await caremapState.setActiveCaremap(careMapId);
                }
                catch (err: any){
                    if (isNoCareMapAccessError(err)){
                        if (isPromoting) {
                            return;
                        }
                        if (isAnonymous) {
                            await signInToOpen.offer();
                        } else {
                            alert.showAlertV2(NO_CARE_MAP_ACCESS_ALERT);
                        }
                        caremapState.unsetCurrentCareMap();
                        history.push('/');
                        return;
                    }
                    logger.error(err);
                }
            }

        });

        return () => {
            // Defer the unset to a microtask so it can't race a cross-section navigation that
            // mounts a drawer for this SAME caremap (e.g. Community → "Update in Care Map"),
            // which would otherwise read an undefined caremap and crash. And skip it entirely
            // when we've navigated to another route still scoped to this caremap.
            Promise.resolve().then(() => {
                const stillScopedToCaremap = Boolean(careMapId) && window.location.hash.includes(`/${careMapId}`);
                if (caremapState.getActiveCaremap()?.careMapId === careMapId && !stillScopedToCaremap){
                    caremapState.unsetCurrentCareMap();
                }
            });
        };
    }, [careMapId, caremapState, isPromoting, history, alert, isAnonymous, logger, signInToOpen]);

    if (caremapState.getActiveCaremap()?.careMapId === careMapId){
        return children;
    }
    return null;
};
